/**
 * Protected Route Component
 *
 * Wraps components that require authentication. Redirects to login if user is not authenticated.
 * Auth pending uses AuthPendingScreen; never AppLoadingScreen until auth is valid.
 */

'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AuthPendingScreen } from '@/shared/components/feedback/AuthPendingScreen';
import { EllipsisErrorBoundary } from '@/shared/components/error/EllipsisErrorBoundary';
import { useAuth } from '../../context/auth-context';
import { isSigningOut } from '@/domains/auth/utils/sign-out';
import { usePathname } from 'next/navigation';

const SKIP_AUTH_PENDING_KEY = 'mechagora:skip-auth-pending';
const RESTORE_MAX_MS = 8_000;

function readSkipAuthPending(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SKIP_AUTH_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSkipAuthPending(): void {
  try {
    sessionStorage.setItem(SKIP_AUTH_PENDING_KEY, '1');
  } catch {
    /* no-op */
  }
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredRole?: string[];
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  requiredRoles = [],
  requiredRole,
  fallback
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [restoreTimedOut, setRestoreTimedOut] = useState(false);
  const roleRequirements = requiredRoles.length > 0 ? requiredRoles : (requiredRole || []);

  const hasRole = React.useCallback((role: string) => {
    if (!user) return false;
    if (user.roles && user.roles.length > 0) {
      return user.roles.includes(role) || user.roles.includes('admin');
    }
    const clearanceLevel = user.clearance_level || 'unclassified';
    return clearanceLevel === 'top_secret' || clearanceLevel === role;
  }, [user]);

  const isAuthPage = pathname?.startsWith('/auth');
  const isClientRoute = pathname?.startsWith('/client');
  const skipAuthPending = readSkipAuthPending();
  const allowDeskWithoutContextAuth = skipAuthPending || restoreTimedOut;
  const showRestore =
    isLoading &&
    !isAuthPage &&
    !isClientRoute &&
    !allowDeskWithoutContextAuth;

  useEffect(() => {
    if (isAuthPage || isClientRoute) {
      return;
    }
    if (isSigningOut()) {
      return;
    }
    if (allowDeskWithoutContextAuth) {
      return;
    }
    if (!isLoading && !isAuthenticated) {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      const returnTo = `${pathname || '/'}${search}`;
      router.replace(
        `/auth/login?error=session_required&return_to=${encodeURIComponent(returnTo)}`
      );
    }
    if (!isLoading && isAuthenticated && roleRequirements.length > 0) {
      const hasRequiredRole = roleRequirements.some(role => hasRole(role));
      if (!hasRequiredRole) {
        router.replace('/dashboard');
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    roleRequirements,
    hasRole,
    router,
    isAuthPage,
    isClientRoute,
    pathname,
    allowDeskWithoutContextAuth,
  ]);

  useEffect(() => {
    if (!isLoading || isAuthPage || isClientRoute || skipAuthPending) {
      setRestoreTimedOut(false);
      return;
    }
    const id = window.setTimeout(() => {
      writeSkipAuthPending();
      setRestoreTimedOut(true);
    }, RESTORE_MAX_MS);
    return () => window.clearTimeout(id);
  }, [isLoading, isAuthPage, isClientRoute, skipAuthPending]);

  if (isSigningOut()) {
    return null;
  }

  if (showRestore) {
    return (
      <EllipsisErrorBoundary>
        <AuthPendingScreen phase="restore" />
      </EllipsisErrorBoundary>
    );
  }

  if (isAuthPage || isClientRoute) {
    return <>{children}</>;
  }

  if (!isAuthenticated && !allowDeskWithoutContextAuth) {
    return fallback ?? null;
  }

  if (isAuthenticated && roleRequirements.length > 0) {
    const hasRequiredRole = roleRequirements.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return fallback || (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <div>
            <h2>Access Denied</h2>
            <p>You don&apos;t have permission to access this page.</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;

export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options: Omit<ProtectedRouteProps, 'children'> = {}
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}

export function usePermissions() {
  const { user } = useAuth();

  const hasRole = (role: string) => {
    if (!user) return false;
    if (user.roles && user.roles.length > 0) {
      return user.roles.includes(role) || user.roles.includes('admin');
    }
    const clearanceLevel = user.clearance_level || 'unclassified';
    return clearanceLevel === 'top_secret' || clearanceLevel === role;
  };

  return {
    user,
    hasRole,
    isAdmin: hasRole('admin'),
    isTrader: hasRole('trader'),
    isAnalyst: hasRole('analyst'),
    isComplianceOfficer: hasRole('compliance_officer'),
    isRiskManager: hasRole('risk_manager'),
    isSystemAuditor: hasRole('system_auditor'),
  };
}
